import { useRouter } from 'next/router';
import { useMemo, useCallback, ReactNode } from 'react';
import { useFilterQueryParser } from './useFilterQueryParser';
import { useFilterState } from './useFilterState';
import { useFilterNavigation } from './useFilterNavigation';
import type { IFilterButton } from '@/creditflowresearch/ui/common/components/organisms/ButtonGroup';
import { extractActiveItems } from './filterHelpers';

export interface IFilterClickContext {
  searchQuery: string,
  filterName: string,
  queryName: string,
}

export interface IFilterItem {
  id: string,
  abbreviation: string,
  queryName?: string,
  isActive: boolean,
  disabled?: boolean,
  tooltip?: string | ReactNode,
  restrictedTooltip?: string | ReactNode,
  onClick?: (context: IFilterClickContext) => void,
}

export interface IFilterSet {
  key: string,
  items: IFilterItem[],
  isVisible?: boolean,
  allowEmpty?: boolean,
  defaultActive?: string[],
}

export interface IUseFilterButtonsOptions {
  filterSet: IFilterSet[],
  isDisabled?: boolean,
  onToggle?: (searchQuery: string, filterName: string, queryName: string) => void,
}

export interface IFilterButtonSet {
  key: string,
  items: IFilterButton[],
}

export interface IActiveItem {
  hasAccess: boolean,
}

export interface IUseFilterButtonsResult {
  filterSets: IFilterButtonSet[],
  getActiveFilters: (filterKey: string) => string[],
  getActiveItems: (filterKey: string) => IActiveItem[],
}

export const useFilterButtons = ({
  filterSet,
  isDisabled = false,
  onToggle,
}: IUseFilterButtonsOptions): IUseFilterButtonsResult => {
  const router = useRouter();
  const { getCurrentAbbreviations: getQueryAbbreviations } = useFilterQueryParser();

  const getCurrentAbbreviations = useCallback((filterKey: string): string[] => {
    const queryAbbreviations = getQueryAbbreviations(filterKey);

    if (queryAbbreviations.length > 0) {
      return queryAbbreviations;
    }

    return filterSet.find(({ key }) => key === filterKey)?.defaultActive ?? queryAbbreviations;
  }, [getQueryAbbreviations, filterSet]);

  const { toggleFilter, getActiveFilters } = useFilterState({ getCurrentAbbreviations });
  const { navigateToFilter } = useFilterNavigation();

  const applyFilter = useCallback((
    filterKey: string,
    queryName: string,
    allowEmpty: boolean,
    onClickItem?: IFilterItem['onClick'],
  ) => {
    if (isDisabled) {
      return;
    }

    const selectedFilterList = toggleFilter(filterKey, queryName, allowEmpty);

    if (!selectedFilterList) {
      return;
    }

    const filterValue = selectedFilterList.join(',');
    const { newUrl, searchQuery } = navigateToFilter(filterKey, filterValue);

    if (onClickItem) {
      onClickItem({ searchQuery, filterName: filterKey, queryName });
      return;
    }

    if (onToggle) {
      onToggle(searchQuery, filterKey, queryName);
      return;
    }

    if (newUrl === router.asPath) {
      return;
    }

    router.push(newUrl, undefined, { shallow: true });
  }, [isDisabled, toggleFilter, navigateToFilter, onToggle]);

  const createButtonItem = useCallback((
    filterData: IFilterSet,
    filter: IFilterItem,
  ): IFilterButton => {
    const currentAbbreviations = getActiveFilters(filterData.key);
    const queryName = filter.queryName ?? filter.abbreviation;
    const isActive = currentAbbreviations.includes(queryName);

    return {
      key: `${ filterData.key }-${ filter.id }`,
      title: filter.abbreviation,
      dataTest: 'togglingFilterApplyButton',
      isActive,
      hasAccess: !filter.disabled,
      tooltip: filter.disabled ? filter.restrictedTooltip : filter.tooltip,
      onClick: () => applyFilter(filterData.key, queryName, Boolean(filterData.allowEmpty), filter.onClick),
    };
  }, [getActiveFilters, applyFilter]);

  const filterSets: IFilterButtonSet[] = useMemo(() => {
    return filterSet
      .filter((filterData) => filterData.isVisible === undefined || filterData.isVisible === true)
      .map((filterData) => {
        const items = filterData.items.map((filter) => createButtonItem(filterData, filter));

        return {
          key: filterData.key,
          items,
        };
      });
  }, [filterSet, createButtonItem]);

  const getActiveItems = useCallback((filterKey: string): IActiveItem[] => {
    const filterSet = filterSets.find((set) => set.key === filterKey);
    if (!filterSet) {
      return [];
    }

    return extractActiveItems(filterSet.items);
  }, [filterSets]);

  return {
    filterSets,
    getActiveFilters,
    getActiveItems,
  };
};
